import { t } from "i18next";
import { create } from "zustand";
import { BackgroundVisitUploadStatus } from "@/common/enums/backgroundVisitUploadStatus";
import { toast } from "@/hooks/use-toast";
import {
  abandonAudioVisitSubmission,
  prepareAudioVisitSubmission,
  readAudioVisitSubmission,
} from "@/lib/audio-visit-submission";
import {
  type PreparedAudioVisit,
  runAudioVisitUpload,
} from "@/lib/audio-visit-upload";
import { runWithStallTimeout } from "@/lib/upload-watchdog";
import { refreshOwnUsage } from "@/lib/usage-queries";
import {
  type PreparedVisitAudioAttachment,
  prepareVisitAudioAttachmentFile,
  runVisitAudioAttachment,
} from "@/lib/visit-audio-attachment";
import {
  abandonVisitAudioAttachment,
  prepareVisitAudioAttachment,
  readVisitAudioAttachment,
} from "@/lib/visit-audio-attachment-identity";
import type { VisitScope } from "@/lib/visit-queries";
import { getVisitScope, refreshVisitReads } from "@/lib/visit-queries";
import { useAuthStore } from "./auth.store";
import { usePostOnboardingStore } from "./postOnboarding.store";

export interface VisitToUpload {
  title?: string;
  visitPublicId?: string;
  file: Blob;
  accountPublicId: string;
  duration: number;
  language?: string;
  style: string;
  format: string;
  exam: string;
  template: string;
  macroPublicIds?: string[];
  customTemplatePublicId: string | null;
  audioTranscriptionServiceAiId?: string;
  diarize?: boolean;
}

interface BackgroundUploadBase {
  id: string;
  accountUid?: string;
  createdAt: Date;
  status: BackgroundVisitUploadStatus;
  data: VisitToUpload;
  progress: number;
}

interface OwnedUploadBase extends BackgroundUploadBase {
  scope: VisitScope;
  controller: AbortController;
  error: string | null;
}
interface OwnedBackgroundVisitUpload extends OwnedUploadBase {
  kind: "owned";
  prepared: PreparedAudioVisit;
}
interface AttachmentBackgroundUpload extends OwnedUploadBase {
  kind: "attachment";
  prepared: PreparedVisitAudioAttachment;
  rejected: boolean;
}
export type BackgroundVisitUpload =
  | OwnedBackgroundVisitUpload
  | AttachmentBackgroundUpload;
const isOwnedBackgroundUpload = (
  upload: BackgroundVisitUpload,
): upload is OwnedBackgroundVisitUpload | AttachmentBackgroundUpload =>
  upload.kind === "owned" || upload.kind === "attachment";
export const isBackgroundUploadVisible = (
  upload: BackgroundVisitUpload,
  scope: VisitScope | null,
): boolean => upload.scope === scope;

export interface BackgroundVisitUploadStoreState {
  uploads: BackgroundVisitUpload[];
  enqueueOwnedAudioVisit: (
    prepared: PreparedAudioVisit,
    scope: VisitScope,
  ) => Promise<void>;
  abandonOwnedAudioVisit: (scope: VisitScope) => void;
  enqueueVisitToUpload: (
    visitToUpload: VisitToUpload,
    options?: { autoStart?: boolean; accountUid?: string },
  ) => Promise<void>;
  restartRejectedAudioAttachment: (id: string) => Promise<void>;
  cancelVisitToUpload: (id: string) => void;
  startVisitToUpload: (id: string) => Promise<void>;
}

export const useBackgroundVisitUploadStore =
  create<BackgroundVisitUploadStoreState>()((set, get) => {
    const getBackgroundUploadById = (
      id: string,
    ): BackgroundVisitUpload | undefined => {
      return get().uploads.find((upload) => upload.id === id);
    };

    const refreshVisits = (
      uploadScope: ReturnType<typeof getVisitScope>,
    ): void => {
      const userData = useAuthStore.getState().userData;
      if (userData) {
        void refreshVisitReads(uploadScope);
        void refreshOwnUsage(uploadScope);
        toast({
          variant: "success",
          title: t("visits.sidebar.messages.uploadSuccess.title"),
          description: t("visits.sidebar.messages.uploadSuccess.description"),
        });
      }
    };

    return {
      uploads: [],
      enqueueOwnedAudioVisit: async (prepared, scope): Promise<void> => {
        if (getVisitScope() !== scope)
          throw Error("Account changed before audio upload");
        const account = `${scope.uid}:${scope.accountPublicId}`;
        const identity = prepareAudioVisitSubmission(
          account,
          prepared.fingerprint,
        );
        const existing = getBackgroundUploadById(identity.visitKey);
        if (existing?.kind === "owned" && existing.scope === scope) {
          if (
            existing.status !== BackgroundVisitUploadStatus.UPLOADING &&
            existing.status !== BackgroundVisitUploadStatus.DONE
          )
            void get().startVisitToUpload(existing.id);
          return;
        }
        const upload: OwnedBackgroundVisitUpload = {
          id: identity.visitKey,
          kind: "owned",
          scope,
          accountUid: scope.uid,
          createdAt: new Date(),
          status: BackgroundVisitUploadStatus.PENDING,
          controller: new AbortController(),
          prepared,
          error: null,
          progress: 0,
          data: {
            ...prepared.body,
            file: prepared.file,
            accountPublicId: scope.accountPublicId,
            customTemplatePublicId:
              prepared.body.customTemplatePublicId ?? null,
          },
        };
        set({
          uploads: [
            ...get().uploads.filter((value) => value.id !== upload.id),
            upload,
          ],
        });
        void get().startVisitToUpload(upload.id);
      },
      abandonOwnedAudioVisit: (scope): void => {
        if (getVisitScope() !== scope)
          throw Error("Account changed before abandoning audio submission");
        abandonAudioVisitSubmission(`${scope.uid}:${scope.accountPublicId}`);
        for (const upload of get().uploads) {
          if (
            upload.kind === "owned" &&
            upload.scope.uid === scope.uid &&
            upload.scope.accountPublicId === scope.accountPublicId
          )
            upload.controller.abort();
        }
        set({
          uploads: get().uploads.filter(
            (upload) =>
              upload.kind !== "owned" ||
              upload.scope.uid !== scope.uid ||
              upload.scope.accountPublicId !== scope.accountPublicId,
          ),
        });
      },
      startVisitToUpload: async (id: string): Promise<void> => {
        const currentUpload = getBackgroundUploadById(id);
        if (currentUpload && isOwnedBackgroundUpload(currentUpload)) {
          if (
            currentUpload.scope !== getVisitScope() ||
            currentUpload.status === BackgroundVisitUploadStatus.UPLOADING ||
            currentUpload.status === BackgroundVisitUploadStatus.DONE
          )
            return;
          const controller = new AbortController();
          const isCurrent = (): boolean =>
            getVisitScope() === currentUpload.scope &&
            getBackgroundUploadById(id)?.controller === controller;
          const patch = (
            changes: Partial<
              Pick<OwnedUploadBase, "status" | "progress" | "error">
            > & { rejected?: boolean },
          ): void => {
            if (isCurrent())
              set({
                uploads: get().uploads.map((upload) =>
                  upload.id === id && isOwnedBackgroundUpload(upload)
                    ? { ...upload, ...changes }
                    : upload,
                ),
              });
          };
          set({
            uploads: get().uploads.map((upload) =>
              upload.id === id && isOwnedBackgroundUpload(upload)
                ? {
                    ...upload,
                    controller,
                    status: BackgroundVisitUploadStatus.UPLOADING,
                    error: null,
                  }
                : upload,
            ),
          });
          try {
            const account = `${currentUpload.scope.uid}:${currentUpload.scope.accountPublicId}`;
            const onProgress = ({
              loaded,
              total,
            }: {
              loaded: number;
              total: number;
            }) => {
              if (!controller.signal.aborted)
                patch({ progress: Math.floor((loaded / total) * 100) });
            };
            // A stalled request (e.g. Wi-Fi lost mid-upload) fails the attempt
            // instead of leaving it "uploading" forever; Retry reuses the key.
            await runWithStallTimeout(
              async ({ signal, touch }) => {
                const report = (progress: {
                  loaded: number;
                  total: number;
                }): void => {
                  touch();
                  onProgress(progress);
                };
                if (currentUpload.kind === "owned") {
                  await runAudioVisitUpload({
                    account,
                    visitKey: currentUpload.id,
                    prepared: currentUpload.prepared,
                    userUid: currentUpload.scope.uid,
                    signal,
                    isCurrent,
                    onProgress: report,
                  });
                } else {
                  await runVisitAudioAttachment({
                    account,
                    key: currentUpload.id,
                    prepared: currentUpload.prepared,
                    userUid: currentUpload.scope.uid,
                    signal,
                    isCurrent,
                    onProgress: report,
                    allowFresh: true,
                  });
                }
              },
              { signal: controller.signal },
            );
            if (!isCurrent() || controller.signal.aborted) return;
            patch({ status: BackgroundVisitUploadStatus.DONE, progress: 100 });
            usePostOnboardingStore.getState().markDone();
            refreshVisits(currentUpload.scope);
          } catch (error) {
            if (!isCurrent()) return;
            let uncertain = true,
              rejected = false;
            try {
              const account = `${currentUpload.scope.uid}:${currentUpload.scope.accountPublicId}`;
              const state =
                currentUpload.kind === "owned"
                  ? readAudioVisitSubmission(account)
                  : readVisitAudioAttachment(
                      account,
                      currentUpload.prepared.parentPublicId,
                    );
              uncertain = state?.kind === "accepting";
              rejected = state?.kind === "rejected";
            } catch {}
            patch({
              status:
                controller.signal.aborted && !uncertain
                  ? BackgroundVisitUploadStatus.CANCELLED
                  : BackgroundVisitUploadStatus.FAILED,
              ...(currentUpload.kind === "attachment" ? { rejected } : {}),
              error: uncertain
                ? t("backgroundVisitUpload.errors.mayHaveSucceeded")
                : rejected
                  ? t("backgroundVisitUpload.errors.sourceChanged")
                  : error instanceof Error
                    ? error.message
                    : t("backgroundVisitUpload.errors.notConfirmed"),
            });
          }
          return;
        }
      },
      enqueueVisitToUpload: async (
        visitToUpload,
        options = { autoStart: true },
      ): Promise<void> => {
        const captured = getVisitScope();
        const isCurrent = (): boolean =>
          getVisitScope() === captured &&
          (!options.accountUid ||
            (useAuthStore.getState().user?.uid === options.accountUid &&
              useAuthStore.getState().userData?.public_id ===
                visitToUpload.accountPublicId));
        if (!isCurrent())
          throw new Error("Account changed before audio upload");
        const parentPublicId = visitToUpload.visitPublicId;
        if (parentPublicId !== undefined) {
          if (
            !captured ||
            captured.accountPublicId !== visitToUpload.accountPublicId
          )
            throw Error("Account changed before audio attachment");
          const prepared = await prepareVisitAudioAttachmentFile({
            parentPublicId,
            file: visitToUpload.file,
          });
          if (!isCurrent() || getVisitScope() !== captured)
            throw Error("Account changed during audio preparation");
          const identity = prepareVisitAudioAttachment(
            `${captured.uid}:${captured.accountPublicId}`,
            parentPublicId,
            prepared.fingerprint,
          );
          const existing = getBackgroundUploadById(identity.key);
          if (existing?.kind === "attachment" && existing.scope === captured) {
            if (
              options.autoStart !== false &&
              existing.status !== BackgroundVisitUploadStatus.UPLOADING &&
              existing.status !== BackgroundVisitUploadStatus.DONE
            )
              void get().startVisitToUpload(existing.id);
            return;
          }
          const upload: AttachmentBackgroundUpload = {
            id: identity.key,
            kind: "attachment",
            scope: captured,
            accountUid: captured.uid,
            createdAt: new Date(),
            status: BackgroundVisitUploadStatus.PENDING,
            controller: new AbortController(),
            prepared,
            error: null,
            rejected: identity.kind === "rejected",
            progress: 0,
            data: { ...visitToUpload, file: prepared.file },
          };
          set({
            uploads: [
              ...get().uploads.filter((value) => value.id !== upload.id),
              upload,
            ],
          });
          if (options.autoStart !== false)
            void get().startVisitToUpload(upload.id);
          return;
        }
        throw Error("Select an existing visit for an audio attachment.");
      },
      restartRejectedAudioAttachment: async (id): Promise<void> => {
        const upload = getBackgroundUploadById(id);
        if (
          upload?.kind !== "attachment" ||
          upload.scope !== getVisitScope() ||
          upload.status === BackgroundVisitUploadStatus.UPLOADING ||
          !upload.rejected
        )
          throw Error("Review the visit before starting a new attachment.");
        try {
          const account = `${upload.scope.uid}:${upload.scope.accountPublicId}`;
          const state = readVisitAudioAttachment(
            account,
            upload.prepared.parentPublicId,
          );
          if (state && state.kind !== "rejected")
            throw Error("Keep the original attachment retry identity.");
          abandonVisitAudioAttachment(account, upload.prepared.parentPublicId);
          upload.controller.abort();
          const identity = prepareVisitAudioAttachment(
            account,
            upload.prepared.parentPublicId,
            upload.prepared.fingerprint,
          );
          const next: AttachmentBackgroundUpload = {
            ...upload,
            id: identity.key,
            controller: new AbortController(),
            status: BackgroundVisitUploadStatus.PENDING,
            rejected: false,
            error: null,
            progress: 0,
          };
          set({
            uploads: get().uploads.map((value) =>
              value === upload ? next : value,
            ),
          });
          await get().startVisitToUpload(next.id);
        } catch (error) {
          if (upload.scope === getVisitScope())
            set({
              uploads: get().uploads.map((value) =>
                value === upload
                  ? {
                      ...value,
                      error:
                        error instanceof Error
                          ? error.message
                          : t("backgroundVisitUpload.errors.restartFailed"),
                    }
                  : value,
              ),
            });
          throw error;
        }
      },
      cancelVisitToUpload: (id): void => {
        const currentUpload = get().uploads.find((upload) => upload.id === id);
        if (currentUpload && isOwnedBackgroundUpload(currentUpload)) {
          currentUpload.controller.abort();
          let uncertain = true;
          try {
            const account = `${currentUpload.scope.uid}:${currentUpload.scope.accountPublicId}`;
            const state =
              currentUpload.kind === "owned"
                ? readAudioVisitSubmission(account)
                : readVisitAudioAttachment(
                    account,
                    currentUpload.prepared.parentPublicId,
                  );
            uncertain = state?.kind === "accepting";
          } catch {}
          set({
            uploads: get().uploads.map((upload) =>
              upload.id === id && isOwnedBackgroundUpload(upload)
                ? {
                    ...upload,
                    status: uncertain
                      ? BackgroundVisitUploadStatus.FAILED
                      : BackgroundVisitUploadStatus.CANCELLED,
                    error: uncertain
                      ? t("backgroundVisitUpload.errors.mayHaveSucceeded")
                      : t("backgroundVisitUpload.errors.cancelled"),
                  }
                : upload,
            ),
          });
          return;
        }
      },
    };
  });

useAuthStore.subscribe(() => {
  for (const upload of useBackgroundVisitUploadStore.getState().uploads) {
    if (isOwnedBackgroundUpload(upload)) {
      if (
        upload.scope !== getVisitScope() &&
        upload.status !== BackgroundVisitUploadStatus.DONE
      )
        useBackgroundVisitUploadStore.getState().cancelVisitToUpload(upload.id);
    }
  }
});
